import { create } from "zustand";
import { devtools, persist, createJSONStorage } from "zustand/middleware";
import type { SliceGeometryActivityResult } from "@/lib/schemas/temporal.schemas";

type ActivityResults = {
  slice_geometry?: SliceGeometryActivityResult;
};

export type TemporarySliceAssetPhase = "processing" | "loading" | "completed" | "error";
export const TEMP_SLICE_ASSET_ID = "temp-slice-output";

type TemporarySliceAsset = {
  id: typeof TEMP_SLICE_ASSET_ID;
  name: string;
  phase: TemporarySliceAssetPhase;
};

type DownloadProgress = {
  loaded: number;
  total: number;
};

const useSliceWorkflowStore = create<{
  workflowId: string | null;
  runId: string | null;
  // The in-flight slice_geometry update. Held here (and in localStorage) so a reload
  // resumes polling the running slice instead of offering a fresh submit — the slice
  // outlives the request that started it (SW-1556).
  updateId: string | null;
  error: string | null;
  isInitializing: boolean;
  sliceStartedAt: number | null;
  initiatedBy: string | null;
  publishFileName: string | null;
  activityResults: ActivityResults;
  temporarySliceAsset: TemporarySliceAsset | null;
  temporarySliceAssetLoadingState: "idle" | "loading" | "success" | "error";
  temporarySliceAssetProgress: DownloadProgress | null;

  setWorkflowId: (workflowId: string | null) => void;
  setRunId: (runId: string | null) => void;
  setUpdateId: (updateId: string | null) => void;
  setWorkflowInfo: (workflowId: string, runId: string | null) => void;
  setError: (error: string | null) => void;
  setIsInitializing: (isInitializing: boolean, options?: { startedAt?: number | null }) => void;
  setInitiatedBy: (initiatedBy: string | null) => void;
  setPublishFileName: (publishFileName: string | null) => void;
  setActivityResults: (results: ActivityResults) => void;
  updateActivityResult: (activityType: string, result: unknown) => void;
  startTemporarySliceAssetProcessing: (name: string) => void;
  startTemporarySliceAssetLoading: () => void;
  setTemporarySliceAssetProgress: (loaded: number, total: number) => void;
  setTemporarySliceAssetCompleted: () => void;
  setTemporarySliceAssetError: () => void;
  clearTemporarySliceAsset: () => void;
  reset: () => void;
}>()(
  devtools(
    persist(
      (set) => ({
        workflowId: null,
        runId: null,
        updateId: null,
        error: null,
        isInitializing: false,
        sliceStartedAt: null,
        initiatedBy: null,
        publishFileName: null,
        activityResults: {},
        temporarySliceAsset: null,
        temporarySliceAssetLoadingState: "idle",
        temporarySliceAssetProgress: null,

        setWorkflowId: (workflowId) =>
          set({ workflowId }, false, { type: "setWorkflowId", workflowId }),

        setRunId: (runId) => set({ runId }, false, { type: "setRunId", runId }),

        setUpdateId: (updateId) => set({ updateId }, false, { type: "setUpdateId", updateId }),

        setWorkflowInfo: (workflowId, runId) =>
          set({ workflowId, runId }, false, { type: "setWorkflowInfo", workflowId, runId }),

        setError: (error) => set({ error }, false, { type: "setError", error }),

        // `sliceStartedAt` has to stay on whichever clock first anchored the run,
        // because `useElapsedSeconds` subtracts it from the browser's `Date.now()`.
        // The run's own client anchors on click, and the `slice-start` SSE event
        // then arrives carrying the server's timestamp; adopting that would
        // re-anchor to a different clock and the display would jump by the skew
        // between them. So once initializing, the existing anchor wins.
        setIsInitializing: (isInitializing, options) =>
          set(
            (state) =>
              isInitializing
                ? {
                    isInitializing: true,
                    sliceStartedAt: state.isInitializing
                      ? state.sliceStartedAt
                      : (options?.startedAt ?? Date.now()),
                  }
                : { isInitializing: false },
            false,
            { type: "setIsInitializing", isInitializing }
          ),

        setInitiatedBy: (initiatedBy) =>
          set({ initiatedBy }, false, { type: "setInitiatedBy", initiatedBy }),

        setPublishFileName: (publishFileName) =>
          set({ publishFileName }, false, { type: "setPublishFileName", publishFileName }),

        setActivityResults: (activityResults) =>
          set({ activityResults }, false, { type: "setActivityResults" }),

        updateActivityResult: (activityType, result) =>
          set(
            (state) => ({
              activityResults: { ...state.activityResults, [activityType]: result },
            }),
            false,
            { type: "updateActivityResult", activityType }
          ),

        startTemporarySliceAssetProcessing: (name) =>
          set(
            () => ({
              temporarySliceAsset: {
                id: TEMP_SLICE_ASSET_ID,
                name,
                phase: "processing",
              },
              temporarySliceAssetLoadingState: "loading",
              temporarySliceAssetProgress: null,
            }),
            false,
            { type: "startTemporarySliceAssetProcessing" }
          ),

        startTemporarySliceAssetLoading: () =>
          set(
            (state) => ({
              temporarySliceAsset: {
                id: TEMP_SLICE_ASSET_ID,
                name: state.temporarySliceAsset?.name ?? "",
                phase: "loading",
              },
              temporarySliceAssetLoadingState: "loading",
              temporarySliceAssetProgress: null,
            }),
            false,
            { type: "startTemporarySliceAssetLoading" }
          ),

        setTemporarySliceAssetProgress: (loaded, total) =>
          set(
            (state) => {
              if (!state.temporarySliceAsset || state.temporarySliceAsset.phase !== "loading") {
                return {};
              }

              return {
                temporarySliceAssetLoadingState: "loading",
                temporarySliceAssetProgress: { loaded, total },
              };
            },
            false,
            { type: "setTemporarySliceAssetProgress", loaded, total }
          ),

        setTemporarySliceAssetCompleted: () =>
          set(
            (state) => ({
              temporarySliceAsset: {
                id: TEMP_SLICE_ASSET_ID,
                name: state.temporarySliceAsset?.name ?? "",
                phase: "completed",
              },
              temporarySliceAssetLoadingState: "success",
              temporarySliceAssetProgress: null,
            }),
            false,
            { type: "setTemporarySliceAssetCompleted" }
          ),

        setTemporarySliceAssetError: () =>
          set(
            (state) => ({
              temporarySliceAsset: {
                id: TEMP_SLICE_ASSET_ID,
                name: state.temporarySliceAsset?.name ?? "",
                phase: "error",
              },
              temporarySliceAssetLoadingState: "error",
              temporarySliceAssetProgress: null,
            }),
            false,
            { type: "setTemporarySliceAssetError" }
          ),

        clearTemporarySliceAsset: () =>
          set(
            () => ({
              temporarySliceAsset: null,
              temporarySliceAssetLoadingState: "idle",
              temporarySliceAssetProgress: null,
            }),
            false,
            { type: "clearTemporarySliceAsset" }
          ),

        reset: () =>
          set(
            () => ({
              workflowId: null,
              runId: null,
              updateId: null,
              error: null,
              isInitializing: false,
              sliceStartedAt: null,
              initiatedBy: null,
              publishFileName: null,
              activityResults: {},
              temporarySliceAsset: null,
              temporarySliceAssetLoadingState: "idle",
              temporarySliceAssetProgress: null,
            }),
            false,
            { type: "reset" }
          ),
      }),
      {
        name: "slice-workflow",
        storage: createJSONStorage(() =>
          process.env.VITEST
            ? { getItem: () => null, setItem: () => {}, removeItem: () => {} }
            : localStorage
        ),
        // Only persist the fields other tabs need to detect an in-progress slice.
        // Heavy data (activity results, region params, asset blobs) stays in-memory.
        partialize: (state) => ({
          isInitializing: state.isInitializing,
          sliceStartedAt: state.sliceStartedAt,
          workflowId: state.workflowId,
          runId: state.runId,
          updateId: state.updateId,
        }),
      }
    ),
    { name: "SliceWorkflowStore" }
  )
);

export { useSliceWorkflowStore };
